import { API_BASE_URL } from './config';
import {
  ApiError,
  type ApiValidationIssue,
  NetworkError,
  parseValidationIssues
} from './errors';

export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
  detail?: string;
}

// Defined in ./errors so this module can use NetworkError without a cycle;
// re-exported because most callers import it from here.
export { ApiError };

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * Native fetch rejects with a bare `TypeError` for DNS / TLS / offline / CORS.
   * Left unwrapped, every caller's error handling — and every analytics
   * `error_code` derived from it — collapses those to "unknown", which is the
   * same blindness that made auth failures unreadable. See ./errors.
   */
  private static async send(
    input: string,
    init: RequestInit
  ): Promise<Response> {
    try {
      return await fetch(input, init);
    } catch (cause) {
      throw new NetworkError('Could not reach the API', cause);
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorDetail = '';

      let errorType: string | undefined;
      let validationIssues: ApiValidationIssue[] = [];

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.detail || errorMessage;
        errorDetail = errorData.detail || '';
        errorType = errorData.type;
        validationIssues = parseValidationIssues(errorData.errors);
      } catch {
        // If response is not JSON, use the status text
      }

      throw new ApiError(
        errorMessage,
        response.status,
        errorDetail,
        errorType,
        validationIssues
      );
    }

    try {
      return await response.json();
    } catch {
      // If response is not JSON, return empty object
      return {} as T;
    }
  }

  private getAuthHeaders(): HeadersInit {
    // Add authentication headers here when needed
    // const token = getAuthToken();
    // return {
    //   'Authorization': `Bearer ${token}`,
    // };
    return {};
  }

  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await ApiClient.send(`${this.baseURL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
        ...options?.headers
      },
      credentials: 'include',
      ...options
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(
    endpoint: string,
    data?: any,
    options?: RequestInit
  ): Promise<T> {
    const isFormData = data instanceof FormData;

    const response = await ApiClient.send(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers: {
        // Don't set Content-Type for FormData, let browser set it with boundary
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...this.getAuthHeaders(),
        ...options?.headers
      },
      body: isFormData ? data : JSON.stringify(data),
      credentials: 'include',
      ...options
    });

    return this.handleResponse<T>(response);
  }

  async put<T>(
    endpoint: string,
    data?: any,
    options?: RequestInit
  ): Promise<T> {
    const isFormData = data instanceof FormData;

    const response = await ApiClient.send(`${this.baseURL}${endpoint}`, {
      method: 'PUT',
      headers: {
        // Don't set Content-Type for FormData, let browser set it with boundary
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...this.getAuthHeaders(),
        ...options?.headers
      },
      body: isFormData ? data : JSON.stringify(data),
      credentials: 'include',
      ...options
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await ApiClient.send(`${this.baseURL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
        ...options?.headers
      },
      credentials: 'include',
      ...options
    });

    return this.handleResponse<T>(response);
  }

  async patch<T>(
    endpoint: string,
    data?: any,
    options?: RequestInit
  ): Promise<T> {
    const isFormData = data instanceof FormData;

    const response = await ApiClient.send(`${this.baseURL}${endpoint}`, {
      method: 'PATCH',
      headers: {
        // Don't set Content-Type for FormData, let browser set it with boundary
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...this.getAuthHeaders(),
        ...options?.headers
      },
      body: isFormData ? data : JSON.stringify(data),
      credentials: 'include',
      ...options
    });

    return this.handleResponse<T>(response);
  }
}

// Singleton instance
export const apiClient = new ApiClient(API_BASE_URL);

// Export the class for testing or multiple instances if needed
export { ApiClient };
